// src/components/auction/AuctionUnavailableTab.js
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useDeferredValue,
} from 'react';
import { createPortal } from 'react-dom';
import './AuctionUnavailableTab.css';
import {
  ArrowRightCircle,
  ExternalLink,
  Search,
  Fish,
  Target,
  Bandage,
  Slash,
  Tag,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Plane,
  PiggyBank,
  KeyRound,
  Dices,
  PencilLine,
  SlidersHorizontal,
  StickyNote,
  Award,
  RotateCcw,
  Percent,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import TeamBadge from '../TeamBadge';
import Tooltip from '../Tooltip';
import { RoleChip, fmt, getPlayProbabilityLabel, getPlayProbabilitySortValue } from './auctionCommon';
import { AUTO_NOTE_PREFIX } from '../../firebase/injuriesService';

// ─── COSTANTI ────────────────────────────────────────────────────────────────

const PAGE_SIZE        = 80;
const PAGE_SIZE_MOBILE = 30;
const ROW_ESTIMATE = 40; // altezza stimata della riga a card compatta (era 62 con le vecchie righe a 22 colonne)
const OVERSCAN    = 8;
const MOBILE_QUERY = '(max-width: 780px)';

const CLASSIC_ROLES = ['all', 'P', 'D', 'C', 'A'];
const MANTRA_ROLES  = ['all', 'P', 'Dd', 'Dc', 'Ds', 'B', 'E', 'M', 'C', 'W', 'T', 'A', 'Pc'];

const CATEGORY_FILTERS = ['all', 'none', 'trap', 'target', 'injury', 'internationalcup', 'cheap', 'key', 'wager', 'goodstarter', 'custom'];

const SORT_OPTIONS = [
  { value: 'value_desc',          label: 'Valore ↓' },
  { value: 'value_asc',           label: 'Valore ↑' },
  { value: 'tier_asc',            label: 'Fascia (Livello)' },
  { value: 'new_first',           label: 'Nuovi giocatori' },
  { value: 'playprob_desc',       label: 'Probabilità impiego ↓' },
  { value: 'pma_desc',            label: 'PMA ↓' },
  { value: 'fv_desc',             label: 'FV ↓' },
  { value: 'mv_desc',             label: 'MV ↓' },
  { value: 'fv_prev_desc',        label: 'FV stagione precedente ↓' },
  { value: 'mv_prev_desc',        label: 'MV stagione precedente ↓' },
  { value: 'caps_desc',           label: 'PG ↓' },
  { value: 'trend_desc',          label: 'Trend FV ↓' },
  { value: 'last5_desc',          label: 'Forma ultime 5 ↓' },
  { value: 'goals_for_desc',      label: 'Gol ↓' },
  { value: 'assists_desc',        label: 'Assist ↓' },
  { value: 'goals_against_desc',  label: 'Gol subiti ↓' },
  { value: 'saves_desc',          label: 'Rigori parati ↓' },
  { value: 'yc_desc',             label: 'Amm. ↓' },
  { value: 'rc_desc',             label: 'Esp. ↓' },
  { value: 'name',                label: 'Nome A-Z' },
];

const DEFAULT_CUSTOM_LABEL = 'Personalizzata';

const CATEGORY_META = {
  all:               { label: 'Tutte',          description: 'Mostra tutti i giocatori',                          icon: Slash },
  none:              { label: 'Nessuna',         description: 'Nessuna categoria assegnata',                       icon: Tag },
  trap:              { label: 'Esca',            description: 'Giocatore da usare come esca in asta',              icon: Fish },
  target:            { label: 'Obiettivo',       description: 'Giocatore interessante, da tenere monitorato',      icon: Target },
  injury:            { label: 'Infortunio',      description: 'Giocatore soggetto ad infortuni',                   icon: Bandage },
  internationalcup:  { label: 'Coppa Internaz.', description: 'Giocatore convocabile per competizioni internaz.',  icon: Plane },
  cheap:             { label: 'Economico',       description: 'Costo inferiore al valore reale',                   icon: PiggyBank },
  key:               { label: 'Chiave',          description: 'Giocatore che deve essere tuo',                     icon: KeyRound },
  wager:             { label: 'Scommessa',       description: 'Potrebbe over-performare',                          icon: Dices },
  goodstarter:       { label: 'Buon titolare',   description: 'Titolare fisso nella sua squadra',                  icon: ShieldCheck },
  custom:            { label: DEFAULT_CUSTOM_LABEL, description: 'Etichetta personalizzata', icon: Sparkles },
};

// ─── FASCE ────────────────────────────────────────────────────────────────
// Fascia di prezzo/priorità assegnata manualmente dall'operatore: 1 (top) .. 5
// (più economica/scarto). 0 = nessuna fascia assegnata.

const TIER_FILTERS = ['all', 'none', '1', '2', '3', '4', '5'];

const TIER_META = {
  none: { label: 'Nessuna',  description: 'Nessuna fascia assegnata' },
  1:    { label: 'Fascia 1', description: 'Livello massimo' },
  2:    { label: 'Fascia 2', description: 'Livello alto' },
  3:    { label: 'Fascia 3', description: 'Livello medio' },
  4:    { label: 'Fascia 4', description: 'Livello basso' },
  5:    { label: 'Fascia 5', description: 'Livello minimo' },
};

function getTierMeta(tier) {
  const n = Number(tier);
  return TIER_META[n >= 1 && n <= 5 ? n : 'none'];
}

// ─── UTILITIES ────────────────────────────────────────────────────────────────

function normalizeText(value = '') {
  return String(value).toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function fmtStat(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

const MANTRA_TOKEN_PATTERN = /(Pc|Dd|Dc|Ds|B|E|M|C|W|T|A|P)/gi;

function normalizeMantraToken(token = '') {
  const m = { pc:'Pc', dd:'Dd', dc:'Dc', ds:'Ds', w:'W', t:'T', m:'M', e:'E', c:'C', a:'A', b:'B', p:'P' };
  return m[String(token || '').trim().toLowerCase()] || String(token || '').trim();
}

function splitRoleTokens(value = '') {
  const raw = String(value || '').trim();
  if (!raw) return [];
  const unique = [];
  raw.split(/[\/,|+]/g).forEach(segment => {
    const part = segment.replace(/\s+/g, '').trim();
    if (!part) return;
    const matches = part.match(MANTRA_TOKEN_PATTERN);
    if (matches && matches.join('').length === part.length) {
      matches.map(t => normalizeMantraToken(t)).filter(Boolean).forEach(token => {
        if (!unique.includes(token)) unique.push(token);
      });
      return;
    }
    let idx = 0;
    while (idx < part.length) {
      const two = normalizeMantraToken(part.slice(idx, idx + 2));
      if (two) { if (!unique.includes(two)) unique.push(two); idx += 2; continue; }
      const one = normalizeMantraToken(part.slice(idx, idx + 1));
      if (one && !unique.includes(one)) unique.push(one);
      idx += 1;
    }
  });
  return unique;
}

function getRoleTokens(roleValue, rolesMode) {
  if (rolesMode !== 'mantra') {
    const role = String(roleValue || '').trim().toUpperCase();
    return role ? [role] : [];
  }
  if (Array.isArray(roleValue)) return roleValue.map(r => normalizeMantraToken(r)).filter(Boolean);
  return splitRoleTokens(roleValue).map(normalizeMantraToken).filter(Boolean);
}

function getMantraBucket(roleValue = '') {
  const tokens = splitRoleTokens(roleValue).map(t => String(t || '').trim().toUpperCase());
  if (tokens.includes('A')) return 'A';
  if (tokens.includes('P')) return 'P';
  if (tokens.some(t => ['DD','DC','DS','B'].includes(t))) return 'D';
  if (tokens.some(t => ['E','W','T','M','C'].includes(t))) return 'C';
  return 'A';
}

function isGoalkeeper(roleValue, rolesMode) {
  const tokens = getRoleTokens(roleValue, rolesMode);
  return tokens.includes('P') && tokens.length === 1;
}

function getCategoryMeta(category = 'none', customLabel) {
  const meta = CATEGORY_META[category] || CATEGORY_META.none;
  if (category === 'custom' && customLabel && customLabel.trim()) {
    return { ...meta, label: customLabel.trim() };
  }
  return meta;
}

// Rileva viewport mobile via matchMedia (aggiornato al resize/rotazione)
function useIsMobile(query = MOBILE_QUERY) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = e => setIsMobile(e.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isMobile;
}

// ─── ACCESSO STATISTICHE ──────────────────────────────────────────────────────

function getSrc(player, season) {
  return season === 'previous' ? (player?.previousSeasonStats || null) : (player || null);
}

function getPma(player, season) {
  const s = getSrc(player, season) || {};
  return s?.pma ?? s?.pmaCurrent ?? s?.averagePma ?? s?.value ?? null;
}

function getCaps(player, season) { return getSrc(player, season)?.caps ?? null; }
function getMv(player, season)   { return getSrc(player, season)?.markavg ?? null; }
function getFv(player, season)   { return getSrc(player, season)?.fmarkavg ?? null; }

function getGoalsFor(player, rolesMode, season) {
  if (isGoalkeeper(player?.role, rolesMode)) return null;
  return getSrc(player, season)?.goals ?? null;
}

function getGoalsAgainst(player, rolesMode, season) {
  if (!isGoalkeeper(player?.role, rolesMode)) return null;
  const s = getSrc(player, season) || {};
  return s?.gotgoals ?? s?.goalsConceded ?? s?.goals_against ?? null;
}

function getAssists(player, rolesMode, season) {
  if (isGoalkeeper(player?.role, rolesMode)) return null;
  return getSrc(player, season)?.assists ?? null;
}

function getSaves(player, rolesMode, season) {
  if (!isGoalkeeper(player?.role, rolesMode)) return null;
  const s = getSrc(player, season) || {};
  return s?.savedPenalties ?? s?.penaltiesSaved ?? s?.penalties_saved ?? s?.saves ?? null;
}

// Rigori: "segnati"/"sbagliati" sono le due sorti di un rigore calciato, quindi
// hanno senso solo per i giocatori di movimento (i portieri li parano, non li
// calciano); "calciati" è la somma delle due.
function getPenaltiesScored(player, rolesMode, season) {
  if (isGoalkeeper(player?.role, rolesMode)) return null;
  return getSrc(player, season)?.penalties ?? null;
}

function getPenaltiesMissed(player, rolesMode, season) {
  if (isGoalkeeper(player?.role, rolesMode)) return null;
  return getSrc(player, season)?.mpenalties ?? null;
}

function getPenaltiesTaken(player, rolesMode, season) {
  if (isGoalkeeper(player?.role, rolesMode)) return null;
  const s = getSrc(player, season) || {};
  if (s?.penalties == null && s?.mpenalties == null) return null;
  return Number(s?.penalties || 0) + Number(s?.mpenalties || 0);
}

function getStats(player, rolesMode, season) {
  const s = getSrc(player, season) || {};
  return {
    pma:             getPma(player, season),
    mv:              getMv(player, season),
    fv:              getFv(player, season),
    caps:            getCaps(player, season),
    goalsFor:        getGoalsFor(player, rolesMode, season),
    goalsAgainst:    getGoalsAgainst(player, rolesMode, season),
    assists:         getAssists(player, rolesMode, season),
    saves:           getSaves(player, rolesMode, season),
    penaltiesTaken:  getPenaltiesTaken(player, rolesMode, season),
    penaltiesScored: getPenaltiesScored(player, rolesMode, season),
    penaltiesMissed: getPenaltiesMissed(player, rolesMode, season),
    yc:              s?.ycards ?? null,
    rc:              s?.rcards ?? null,
    ag:              s?.owngoals ?? null,
  };
}

// ─── SCORE SINTETICO ─────────────────────────────────────────────────────────
// Punteggio 0–100 per ordinare rapidamente i giocatori migliori disponibili.
// Usa dati di entrambe le stagioni con peso maggiore sulla corrente.

// Trend: differenza FV corrente vs precedente
function computeFvTrend(player) {
  const fvC = toNum(getFv(player, 'current'));
  const fvP = toNum(getFv(player, 'previous'));
  if (!fvC && !fvP) return 0;
  if (!fvP) return 0;
  return fvC - fvP;
}

// Forma nelle ultime 5 giornate (stessa formula usata in PlayersPage per coerenza)
function getLast5Score(player) {
  const l5 = player?.last5 || {};
  return (
    Number(l5.markavg || 0) * 0.45 +
    Number(l5.fmarkavg || 0) * 0.35 +
    Number(l5.goals || 0) * 0.1 +
    Number(l5.assists || 0) * 0.1
  );
}

// ─── COMPONENTI UI PICCOLI ────────────────────────────────────────────────────

// Stesso identico "look" del chip "Nuovo" (.sv-new-chip): solo testo, pillola
// piccola, un colore per categoria — niente più icona in un riquadro a parte.
function CategoryChip({ category, showTooltip = true, customLabel }) {
  const meta = getCategoryMeta(category, customLabel);
  return (
    <Tooltip as="span" className={`sv-category-chip sv-category-chip--${category}`} content={showTooltip ? meta.description : undefined}>
      {meta.label}
    </Tooltip>
  );
}

function TierChip({ tier, showTooltip = true }) {
  const value = Number(tier) >= 1 && Number(tier) <= 5 ? Number(tier) : 0;
  const meta = getTierMeta(value);
  return (
    <Tooltip as="span" className={`sv-tier-chip sv-tier-chip--${value || 'none'}`} content={showTooltip ? meta.description : undefined}>
      <Award size={12} className="sv-tier-chip__icon" aria-hidden="true" />
      <span className="sv-tier-chip__label">{value ? `F${value}` : '—'}</span>
    </Tooltip>
  );
}

function MaxSpendChip({ credits, showTooltip = true }) {
  const value = Number(credits) > 0 ? Number(credits) : 0;
  return (
    <Tooltip as="span" className={`sv-maxspend-chip ${value ? 'is-set' : ''}`} content={showTooltip ? (value ? `Tetto massimo: ${value} cr` : 'Nessun tetto impostato') : undefined}>
      <Percent size={12} className="sv-maxspend-chip__icon" aria-hidden="true" />
      <span className="sv-maxspend-chip__label">{value ? `${value} cr` : '—'}</span>
    </Tooltip>
  );
}

function PlayProbabilityChip({ entry, compact = false }) {
  const label = getPlayProbabilityLabel(entry);
  const variant = !entry || label === '—' ? 'unknown' : entry.status === 'starter' ? 'starter' : 'bench';
  const shortLabel = variant === 'unknown' ? '—' : variant === 'starter' ? 'Tit' : 'Pan';

  return (
    <Tooltip
      as="span"
      className={`sv-playprob-chip sv-playprob-chip--${variant}`}
      content="Probabile formazione FantaMaster per la prossima giornata"
    >
      {compact ? shortLabel : label}
    </Tooltip>
  );
}

function renderRoleBadge(roleValue, rolesMode) {
  if (rolesMode !== 'mantra') {
    return <RoleChip role={String(roleValue || 'A').trim().toUpperCase() || 'A'} />;
  }
  const text   = String(roleValue || '').trim() || '—';
  const bucket = getMantraBucket(text);
  return (
    <span className={`sv-role-pill sv-role-pill--mantra sv-role-pill--mantra-${bucket}`} aria-label={text}>
      {text}
    </span>
  );
}

// ─── CATEGORY POPOVER ─────────────────────────────────────────────────────────

const CategoryPopover = memo(function CategoryPopover({ open, anchorRect, playerName, currentCategory, onSelect, onClose, saving, customLabel, canRenameCustom = false, onRequestRenameCustom }) {
  const menuRef = useRef(null);

  const style = useMemo(() => {
    if (!anchorRect || typeof window === 'undefined') return { top: 24, left: 24, width: 380 };
    const margin = 12, width = 380;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = anchorRect.left;
    if (left + width > vw - margin) left = vw - width - margin;
    if (left < margin) left = margin;
    const estimatedH = 340;
    let top = anchorRect.bottom + 8;
    if (top + estimatedH > vh - margin) top = Math.max(margin, anchorRect.top - estimatedH - 8);
    return { top, left, width };
  }, [anchorRect]);

  useEffect(() => {
    if (!open) return;
    const down  = e => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    const key   = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', down, true); document.removeEventListener('keydown', key); };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const items = ['none','trap','target','injury','internationalcup','cheap','key','wager','goodstarter','custom'];

  return createPortal(
    <div className="sv-picker-layer">
      <div
        ref={menuRef}
        className="sv-picker glass"
        style={{ position: 'fixed', top: style.top, left: style.left, width: style.width }}
        role="dialog"
        aria-label={`Seleziona categoria per ${playerName}`}
      >
        <div className="sv-picker__head">
          <div>
            <strong>{playerName}</strong>
            <span>Seleziona una categoria</span>
          </div>
          <button type="button" className="sv-picker__close" onClick={onClose} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>

        <div className="sv-picker__list sv-picker__list--grid">
          {items.map(category => {
            const meta   = getCategoryMeta(category, customLabel);
            const Icon   = meta.icon;
            const active = category === currentCategory;

            return (
              <Tooltip
                as="button"
                key={category}
                type="button"
                className={['sv-category-option', `sv-category-option--${category}`, active ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => onSelect(category)}
                disabled={saving}
                content={meta.description}
              >
                <span className="sv-category-option__icon" aria-hidden="true">{Icon ? <Icon size={14} /> : null}</span>
                <span className="sv-category-option__body">
                  <strong>{meta.label}</strong>
                  <small>{meta.description}</small>
                </span>
                {category === 'custom' && canRenameCustom && (
                  <span
                    className="sv-category-option__rename"
                    role="button"
                    tabIndex={0}
                    aria-label="Rinomina categoria personalizzata"
                    onClick={(e) => { e.stopPropagation(); onRequestRenameCustom?.(meta.label); }}
                  >
                    <PencilLine size={13} />
                  </span>
                )}
                {active && <span className="sv-category-option__activeDot" />}
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
});

// ─── MODALE RINOMINA CATEGORIA "PERSONALIZZATA" ────────────────────────────────

function RenameCustomCategoryModal({ initialLabel, onClose, onSubmit, saving }) {
  const [value, setValue] = useState(initialLabel || '');
  const isValid = value.trim().length > 0;

  return (
    <div className="sv-modal-backdrop" onClick={onClose}>
      <div className="sv-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="sv-modal__head">
          <div>
            <div className="sv-modal__eyebrow"><Sparkles size={14} />Categoria personalizzata</div>
            <h2>Rinomina la categoria</h2>
          </div>
          <button className="sv-modal__close" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        <form className="sv-modal-form" onSubmit={(e) => { e.preventDefault(); if (isValid) onSubmit(value.trim()); }}>
          <label>
            <span>Nome categoria</span>
            <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={24} autoFocus required />
          </label>
          <div className="sv-modal-form__actions">
            <button type="button" className="sv-modal-cancel-btn" onClick={onClose}>Annulla</button>
            <button className="sv-modal-save-btn" type="submit" disabled={saving || !isValid}>
              {saving ? <><Loader2 size={16} className="sv-spinner" /> Salvo...</> : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── TIER POPOVER ─────────────────────────────────────────────────────────────

const TierPopover = memo(function TierPopover({ open, anchorRect, playerName, currentTier, onSelect, onClose, saving }) {
  const menuRef = useRef(null);

  const style = useMemo(() => {
    if (!anchorRect || typeof window === 'undefined') return { top: 24, left: 24, width: 260 };
    const margin = 12, width = 260;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = anchorRect.left;
    if (left + width > vw - margin) left = vw - width - margin;
    if (left < margin) left = margin;
    const estimatedH = 260;
    let top = anchorRect.bottom + 8;
    if (top + estimatedH > vh - margin) top = Math.max(margin, anchorRect.top - estimatedH - 8);
    return { top, left, width };
  }, [anchorRect]);

  useEffect(() => {
    if (!open) return;
    const down  = e => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    const key   = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', down, true); document.removeEventListener('keydown', key); };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const items = [0, 1, 2, 3, 4, 5];

  return createPortal(
    <div className="sv-picker-layer">
      <div
        ref={menuRef}
        className="sv-picker glass"
        style={{ position: 'fixed', top: style.top, left: style.left, width: style.width }}
        role="dialog"
        aria-label={`Seleziona fascia per ${playerName}`}
      >
        <div className="sv-picker__head">
          <div>
            <strong>{playerName}</strong>
            <span>Seleziona una fascia</span>
          </div>
          <button type="button" className="sv-picker__close" onClick={onClose} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>

        <div className="sv-picker__list sv-picker__list--tiers">
          {items.map(tier => {
            const meta   = getTierMeta(tier);
            const active = tier === currentTier;
            return (
              <Tooltip
                as="button"
                key={tier}
                type="button"
                className={['sv-tier-option', `sv-tier-option--${tier || 'none'}`, active ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => onSelect(tier)}
                disabled={saving}
                content={meta.description}
              >
                <strong>{tier ? `Fascia ${tier}` : 'Nessuna'}</strong>
                {active && <span className="sv-category-option__activeDot" />}
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
});

// ─── TETTO MASSIMO POPOVER ──────────────────────────────────────────────────

const MaxSpendPopover = memo(function MaxSpendPopover({ open, anchorRect, playerName, currentCredits, onSelect, onClose, saving }) {
  const menuRef = useRef(null);
  const [value, setValue] = useState('');

  const style = useMemo(() => {
    if (!anchorRect || typeof window === 'undefined') return { top: 24, left: 24, width: 280 };
    const margin = 12, width = 280;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = anchorRect.left;
    if (left + width > vw - margin) left = vw - width - margin;
    if (left < margin) left = margin;
    const estimatedH = 190;
    let top = anchorRect.bottom + 8;
    if (top + estimatedH > vh - margin) top = Math.max(margin, anchorRect.top - estimatedH - 8);
    return { top, left, width };
  }, [anchorRect]);

  useEffect(() => {
    if (open) setValue(currentCredits ? String(currentCredits) : '');
  }, [open, currentCredits]);

  useEffect(() => {
    if (!open) return;
    const down  = e => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    const key   = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('pointerdown', down, true); document.removeEventListener('keydown', key); };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="sv-picker-layer">
      <div
        ref={menuRef}
        className="sv-picker glass"
        style={{ position: 'fixed', top: style.top, left: style.left, width: style.width }}
        role="dialog"
        aria-label={`Imposta tetto massimo per ${playerName}`}
      >
        <div className="sv-picker__head">
          <div>
            <strong>{playerName}</strong>
            <span>Tetto massimo di spesa (crediti)</span>
          </div>
          <button type="button" className="sv-picker__close" onClick={onClose} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>

        <label className="sv-maxspend-field">
          <input
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="Es. 45"
            autoFocus
          />
          <span>cr</span>
        </label>

        <div className="sv-picker__actions">
          <button type="button" className="sv-picker__actionBtn" onClick={() => onSelect(0)} disabled={saving}>
            Rimuovi
          </button>
          <button type="button" className="sv-picker__actionBtn sv-picker__actionBtn--primary" onClick={() => onSelect(Number(value) || 0)} disabled={saving}>
            Salva
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
});

// ─── PLAYER ROW ───────────────────────────────────────────────────────────────

// Riga a "card compatta" (sostituisce la vecchia riga a 22 colonne dentro
// <table>): identità + le 3 etichette curabili a mano (categoria/fascia/
// tetto, invariate come meccanismo — stesso Tooltip/popover di sempre, solo
// più piccole) + tutte le statistiche numeriche (stesso ordine fisso per
// ogni riga, vedi commento su .sv-row__stats) + azioni. Solo calendario
// prossime partite e confronto con la stagione precedente restano nella
// scheda giocatore completa (click sulla riga) — troppo ingombranti per
// stare su una riga sola.
const PlayerRow = memo(function PlayerRow({
  player, rolesMode, category, saving, tier, savingTier, maxSpend, savingMaxSpend, pma, playProbability,
  onOpenDetail, onOpenInMarket, onOpenCategoryPicker, onOpenTierPicker, onOpenMaxSpendPicker, onOpenNotesEditor,
  playerNotesById = {}, readOnly = false, highlightTiers = false, customCategoryLabel,
}) {
  const curr = useMemo(() => getStats(player, rolesMode, 'current'), [player, rolesMode]);

  const role = useMemo(() => {
    if (rolesMode !== 'mantra') return String(player.role || '').trim().toUpperCase() || '—';
    const tokens = getRoleTokens(player.role, rolesMode);
    return tokens.length ? tokens.join('/') : '—';
  }, [player.role, rolesMode]);

  const noteText = String(playerNotesById?.[String(player.id)] || '').trim();
  const hasNote = Boolean(noteText);
  const isAutoInjuryNote = noteText.startsWith(AUTO_NOTE_PREFIX);
  const isNew = !player.previousSeasonStats;

  const rowCls = [
    'sv-row',
    category === 'key'    ? 'sv-row--key'    : '',
    category === 'target' ? 'sv-row--target' : '',
    category === 'trap'   ? 'sv-row--trap'   : '',
    highlightTiers ? `sv-row--tier-${tier >= 1 && tier <= 5 ? tier : 'none'}` : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rowCls} onClick={() => typeof onOpenDetail === 'function' && onOpenDetail(player.id)}>
      <TeamBadge team={player.team} size={24} />

      <div className="sv-row__main">
        <strong className="sv-row__name">{player.name}</strong>
        <span className="sv-row__meta">
          {renderRoleBadge(role, rolesMode)}
          {isNew && (
            <Tooltip as="span" className="sv-new-chip" content="Giocatore acquistato questa stagione">Nuovo</Tooltip>
          )}
          {hasNote && (
            isAutoInjuryNote
              ? <Bandage size={11} className="sv-row__noteDot sv-row__noteDot--injury" aria-label="Nota automatica sull'infortunio" />
              : <StickyNote size={11} className="sv-row__noteDot" aria-label="Ha una nota" />
          )}
        </span>
      </div>

      <div className="sv-row__tags" onClick={e => e.stopPropagation()}>
        {readOnly ? (
          <CategoryChip category={category} customLabel={customCategoryLabel} />
        ) : (
          <Tooltip
            as="button"
            type="button"
            className={`sv-category-btn ${category !== 'none' ? 'is-tagged' : ''}`}
            onClick={e => { e.stopPropagation(); onOpenCategoryPicker?.(player, e.currentTarget); }}
            disabled={saving}
            content="Cambia categoria"
          >
            <CategoryChip category={category} showTooltip={false} customLabel={customCategoryLabel} />
            {saving && <Loader2 size={12} className="sv-spinner" />}
          </Tooltip>
        )}

        {readOnly ? (
          <TierChip tier={tier} />
        ) : (
          <Tooltip
            as="button"
            type="button"
            className={`sv-tier-btn ${tier ? 'is-tagged' : ''}`}
            onClick={e => { e.stopPropagation(); onOpenTierPicker?.(player, e.currentTarget); }}
            disabled={savingTier}
            content="Cambia fascia"
          >
            <TierChip tier={tier} showTooltip={false} />
            {savingTier && <Loader2 size={12} className="sv-spinner" />}
          </Tooltip>
        )}

        {readOnly ? (
          <MaxSpendChip credits={maxSpend} />
        ) : (
          <Tooltip
            as="button"
            type="button"
            className={`sv-maxspend-btn ${maxSpend ? 'is-tagged' : ''}`}
            onClick={e => { e.stopPropagation(); onOpenMaxSpendPicker?.(player, e.currentTarget); }}
            disabled={savingMaxSpend}
            content="Imposta tetto massimo di spesa"
          >
            <MaxSpendChip credits={maxSpend} showTooltip={false} />
            {savingMaxSpend && <Loader2 size={12} className="sv-spinner" />}
          </Tooltip>
        )}
      </div>

      {/* Statistiche: ordine FISSO e identico per ogni riga, così ogni numero
          resta sempre nella stessa posizione orizzontale (es. "PG" è sempre
          la 6ª colonna, per tutti) — stesso principio della vecchia tabella,
          senza però la sua intestazione: l'etichetta piccola sotto ogni
          numero basta a identificarlo. GF/GS e ASS/RP restano colonne SEMPRE
          presenti per tutti (non si scambiano in base al ruolo): un
          giocatore di movimento avrà semplicemente "—" su GS/RP, un portiere
          "—" su GF/ASS — così la posizione non si sposta mai. */}
      <div className="sv-row__stats">
        <div className="sv-row__stat">
          <strong>{player.value !== null && player.value !== undefined ? fmt(player.value) : '—'}</strong>
          <span>Val</span>
        </div>
        <div className="sv-row__stat">
          <strong>{pma !== null && pma !== undefined ? fmt(pma) : '—'}</strong>
          <span>PMA</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.fv) ?? '—'}</strong>
          <span>FV</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.mv) ?? '—'}</strong>
          <span>MV</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.caps) ?? '—'}</strong>
          <span>PG</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.goalsFor) ?? '—'}</strong>
          <span>GF</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.goalsAgainst) ?? '—'}</strong>
          <span>GS</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.assists) ?? '—'}</strong>
          <span>Ass</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.saves) ?? '—'}</strong>
          <span>RP</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.penaltiesTaken) ?? '—'}</strong>
          <span>RigC</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.penaltiesMissed) ?? '—'}</strong>
          <span>RigE</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.penaltiesScored) ?? '—'}</strong>
          <span>RigS</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.yc) ?? '—'}</strong>
          <span>Amm</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.rc) ?? '—'}</strong>
          <span>Esp</span>
        </div>
        <div className="sv-row__stat">
          <strong>{fmtStat(curr.ag) ?? '—'}</strong>
          <span>AG</span>
        </div>
        <div className="sv-row__stat sv-row__stat--prob">
          <PlayProbabilityChip entry={playProbability} compact />
        </div>
      </div>

      <div className="sv-row__actions" onClick={e => e.stopPropagation()}>
        {!readOnly && (
          <Tooltip
            as="button"
            type="button" className={`sv-action-btn sv-action-btn--note ${hasNote ? 'is-tagged' : ''}`}
            onClick={e => { e.stopPropagation(); onOpenNotesEditor?.(player, e.currentTarget); }}
            content={hasNote ? 'Modifica nota' : 'Aggiungi nota'}
          >
            <PencilLine size={14} />
          </Tooltip>
        )}
        <Tooltip
          as="button"
          type="button" className="sv-action-btn sv-action-btn--market"
          onClick={e => { e.stopPropagation(); onOpenInMarket?.(player.id); }}
          content="Apri nel market"
        >
          <ArrowRightCircle size={14} />
        </Tooltip>
      </div>

      <ChevronRight size={16} className="sv-row__chevron" aria-hidden="true" />
    </div>
  );
});

// ─── CARD MOBILE ──────────────────────────────────────────────────────────────
// Sotto MOBILE_QUERY la tabella larga è illeggibile: ogni giocatore diventa una
// card minimale (Valore, MV, MFV, categoria e azioni).

const MobilePlayerCard = memo(function MobilePlayerCard({
  player, rolesMode, category, saving, tier, savingTier, maxSpend, savingMaxSpend, note = '', playProbability,
  onOpenDetail, onOpenInMarket, onOpenCategoryPicker, onOpenTierPicker, onOpenMaxSpendPicker, onOpenNotesEditor,
  readOnly = false, customCategoryLabel,
}) {
  const curr = useMemo(() => getStats(player, rolesMode, 'current'), [player, rolesMode]);

  const role = useMemo(() => {
    if (rolesMode !== 'mantra') return String(player.role || '').trim().toUpperCase() || '—';
    const tokens = getRoleTokens(player.role, rolesMode);
    return tokens.length ? tokens.join('/') : '—';
  }, [player.role, rolesMode]);

  const noteText = String(note || '').trim();

  const cardCls = [
    'svm-card',
    category === 'key'    ? 'svm-card--key'    : '',
    category === 'target' ? 'svm-card--target' : '',
    category === 'trap'   ? 'svm-card--trap'   : '',
  ].filter(Boolean).join(' ');

  return (
    <article className={cardCls} onClick={() => typeof onOpenDetail === 'function' && onOpenDetail(player.id)}>
      <div className="svm-card__top">
        <TeamBadge team={player.team} size={24} />
        <span className="svm-row__role">{renderRoleBadge(role, rolesMode)}</span>
        <Tooltip as="strong" className="svm-row__name" content={player.name}>{player.name}</Tooltip>
      </div>

      <div className="svm-card__bottom">
        <div className="svm-row__stats">
          <div className="svm-row__stat">
            <span>Val</span>
            <strong>{player.value !== null && player.value !== undefined ? fmt(player.value) : '—'}</strong>
          </div>
          <div className="svm-row__stat">
            <span>MV</span>
            <strong>{fmtStat(curr.mv) ?? '—'}</strong>
          </div>
          <div className="svm-row__stat svm-row__stat--fv">
            <span>MFV</span>
            <strong>{fmtStat(curr.fv) ?? '—'}</strong>
          </div>
        </div>

        <div className="svm-row__cat" onClick={e => e.stopPropagation()}>
          {readOnly ? (
            <CategoryChip category={category} customLabel={customCategoryLabel} />
          ) : (
            <Tooltip
              as="button"
              type="button"
              className={`sv-category-btn ${category !== 'none' ? 'is-tagged' : ''}`}
              onClick={e => { e.stopPropagation(); onOpenCategoryPicker?.(player, e.currentTarget); }}
              disabled={saving}
              content="Cambia categoria"
            >
              <CategoryChip category={category} showTooltip={false} customLabel={customCategoryLabel} />
              {saving && <Loader2 size={12} className="sv-spinner" />}
            </Tooltip>
          )}
        </div>

        <div className="svm-row__tier" onClick={e => e.stopPropagation()}>
          {readOnly ? (
            <TierChip tier={tier} />
          ) : (
            <Tooltip
              as="button"
              type="button"
              className={`sv-tier-btn ${tier ? 'is-tagged' : ''}`}
              onClick={e => { e.stopPropagation(); onOpenTierPicker?.(player, e.currentTarget); }}
              disabled={savingTier}
              content="Cambia fascia"
            >
              <TierChip tier={tier} showTooltip={false} />
              {savingTier && <Loader2 size={12} className="sv-spinner" />}
            </Tooltip>
          )}
        </div>

        <div className="svm-row__maxspend" onClick={e => e.stopPropagation()}>
          {readOnly ? (
            <MaxSpendChip credits={maxSpend} />
          ) : (
            <Tooltip
              as="button"
              type="button"
              className={`sv-maxspend-btn ${maxSpend ? 'is-tagged' : ''}`}
              onClick={e => { e.stopPropagation(); onOpenMaxSpendPicker?.(player, e.currentTarget); }}
              disabled={savingMaxSpend}
              content="Imposta tetto massimo di spesa"
            >
              <MaxSpendChip credits={maxSpend} showTooltip={false} />
              {savingMaxSpend && <Loader2 size={12} className="sv-spinner" />}
            </Tooltip>
          )}
        </div>

        <div className="svm-row__prob" onClick={e => e.stopPropagation()}>
          <PlayProbabilityChip entry={playProbability} compact />
        </div>

        <div className="svm-actions" onClick={e => e.stopPropagation()}>
          <Tooltip
            as="button"
            type="button"
            className="svm-btn svm-btn--market"
            onClick={() => onOpenInMarket?.(player.id)}
            content="Seleziona il giocatore e passa al tab Market"
          >
            <ArrowRightCircle size={13} />
          </Tooltip>
          {!readOnly && (
            <Tooltip
              as="button"
              type="button"
              className={`svm-btn ${noteText ? 'is-tagged' : ''}`}
              onClick={e => onOpenNotesEditor?.(player, e.currentTarget)}
              content={noteText ? 'Modifica nota' : 'Aggiungi nota'}
            >
              <PencilLine size={13} />
            </Tooltip>
          )}
          <Tooltip
            as="button"
            type="button"
            className="svm-btn svm-btn--detail"
            onClick={() => onOpenDetail?.(player.id)}
            content="Apri la scheda giocatore"
          >
            <ExternalLink size={13} />
          </Tooltip>
        </div>
      </div>
    </article>
  );
});

// ─── TABELLA VIRTUALE ─────────────────────────────────────────────────────────

function VirtualBody({
  rows, scrollTop, viewportHeight,
  rolesMode, savingId, localCategoriesById, savingTierId, localTiersById, savingMaxSpendId, localMaxSpendById, marketPmaByPlayerId, playerNotesById,
  playProbabilityById = {}, readOnly = false, highlightTiers = false, customCategoryLabel,
  onOpenDetail, onOpenInMarket, onOpenCategoryPicker, onOpenTierPicker, onOpenMaxSpendPicker, onOpenNotesEditor,
}) {
  const total      = rows.length;
  const startIdx   = Math.max(0, Math.floor(scrollTop / ROW_ESTIMATE) - OVERSCAN);
  const visibleCnt = Math.ceil(viewportHeight / ROW_ESTIMATE) + OVERSCAN * 2;
  const endIdx     = Math.min(total, startIdx + visibleCnt);
  const topSpc     = startIdx * ROW_ESTIMATE;
  const botSpc     = Math.max(0, (total - endIdx) * ROW_ESTIMATE);
  const visible    = rows.slice(startIdx, endIdx);

  return (
    <div className="sv-cardlist">
      {topSpc > 0 && <div className="sv-spacer-row" aria-hidden="true" style={{ height: topSpc }} />}
      {visible.map(player => {
        const pma      = marketPmaByPlayerId?.[String(player.id)]?.average ?? null;
        const category = String(localCategoriesById?.[String(player.id)] || 'none');
        const saving   = String(savingId || '') === String(player.id);
        const tier     = Number(localTiersById?.[String(player.id)] || 0);
        const savingTier = String(savingTierId || '') === String(player.id);
        const maxSpend = Number(localMaxSpendById?.[String(player.id)] || 0);
        const savingMaxSpend = String(savingMaxSpendId || '') === String(player.id);
        return (
          <PlayerRow
            key={player.id}
            player={player}
            rolesMode={rolesMode}
            category={category}
            saving={saving}
            tier={tier}
            savingTier={savingTier}
            maxSpend={maxSpend}
            savingMaxSpend={savingMaxSpend}
            pma={pma}
            playProbability={playProbabilityById?.[String(player.id)]}
            onOpenDetail={onOpenDetail}
            onOpenInMarket={onOpenInMarket}
            onOpenCategoryPicker={onOpenCategoryPicker}
            onOpenTierPicker={onOpenTierPicker}
            onOpenMaxSpendPicker={onOpenMaxSpendPicker}
            onOpenNotesEditor={onOpenNotesEditor}
            playerNotesById={playerNotesById}
            readOnly={readOnly}
            highlightTiers={highlightTiers}
            customCategoryLabel={customCategoryLabel}
          />
        );
      })}
      {botSpc > 0 && <div className="sv-spacer-row" aria-hidden="true" style={{ height: botSpc }} />}
    </div>
  );
}

// ─── PAGINAZIONE ──────────────────────────────────────────────────────────────

function PaginationBar({ page, totalPages, totalItems, fromItem, toItem, onPageChange }) {
  const go = useCallback(n => onPageChange(Math.max(1, Math.min(totalPages, n))), [onPageChange, totalPages]);
  return (
    <div className="sv-pagination">
      <div className="sv-pagination__info">
        <strong>{totalItems === 0 ? 'Nessun record' : `${fromItem}–${toItem} di ${totalItems}`}</strong>
        <span>Pagina {page} / {totalPages}</span>
      </div>
      <div className="sv-pagination__controls">
        <button type="button" className="sv-pg-btn" onClick={() => go(1)}        disabled={page <= 1}><ChevronsLeft  size={15} /></button>
        <button type="button" className="sv-pg-btn" onClick={() => go(page - 1)} disabled={page <= 1}><ChevronLeft   size={15} /></button>
        <label className="sv-pg-field">
          <span>Pag.</span>
          <input type="number" min={1} max={totalPages} value={page} onChange={e => go(Number(e.target.value))} inputMode="numeric" />
        </label>
        <button type="button" className="sv-pg-btn" onClick={() => go(page + 1)} disabled={page >= totalPages}><ChevronRight  size={15} /></button>
        <button type="button" className="sv-pg-btn" onClick={() => go(totalPages)} disabled={page >= totalPages}><ChevronsRight size={15} /></button>
      </div>
    </div>
  );
}

// ─── EXPORT DEFAULT ───────────────────────────────────────────────────────────

export default function AuctionUnavailableTab({
  auction,
  players,
  purchases,
  marketPmaByPlayerId = {},
  rolesMode = 'classic',
  playerCategoriesById = {},
  playerTiersById = {},
  playerMaxSpendById = {},
  playerNotesById = {},
  playProbabilityById = {},
  savingPlayerCategoryId = null,
  onChangePlayerCategory,
  savingPlayerTierId = null,
  onChangePlayerTier,
  savingPlayerMaxSpendId = null,
  onChangePlayerMaxSpend,
  onSavePlayerNote,
  onOpenPlayerDetail,
  onOpenInMarket,
  readOnly = false,
  customCategoryLabel,
  onRenameCustomCategory,
}) {
  // — Dati disponibili (escludi già acquistati)
  const purchasedSet = useMemo(() => {
    const s = new Set();
    (purchases || []).filter(p => !p.deleted).forEach(p => s.add(String(p.playerId)));
    return s;
  }, [purchases]);

  const available = useMemo(
    () => (players || []).filter(p => !purchasedSet.has(String(p.id))),
    [players, purchasedSet]
  );

  // — Trend precalcolati
  const trendMap = useMemo(() => {
    const m = {};
    available.forEach(p => { m[String(p.id)] = computeFvTrend(p); });
    return m;
  }, [available]);

  // — Stato UI
  const [search,          setSearch]         = useState('');
  const dSearch                              = useDeferredValue(search);
  const [selectedRoles,   setSelectedRoles]  = useState([]); // vuoto = tutti i ruoli
  const [teamFilter,      setTeamFilter]     = useState('all');
  const [catFilter,       setCatFilter]      = useState('all');
  const [tierFilter,      setTierFilter]     = useState('all');
  const [sortMode,        setSortMode]       = useState('value_desc');
  const [showFilterPanel, setShowFilterPanel]= useState(false);
  const [page,            setPage]           = useState(1);
  const [pickerState,     setPickerState]    = useState(null);
  const [tierPickerState, setTierPickerState]= useState(null);
  const [maxSpendPickerState, setMaxSpendPickerState] = useState(null);
  const [notesState,      setNotesState]     = useState(null);
  const [renamingCustomCategory, setRenamingCustomCategory] = useState(false);
  const [savingCustomCategoryLabel, setSavingCustomCategoryLabel] = useState(false);

  const openRenameCustomCategory = useCallback(() => {
    setPickerState(null);
    setRenamingCustomCategory(true);
  }, []);

  const submitRenameCustomCategory = useCallback(async (value) => {
    setSavingCustomCategoryLabel(true);
    try {
      await onRenameCustomCategory?.(value);
      setRenamingCustomCategory(false);
    } finally {
      setSavingCustomCategoryLabel(false);
    }
  }, [onRenameCustomCategory]);

  const [localCats, setLocalCats] = useState(() => ({ ...(playerCategoriesById || {}) }));
  const [localTiers, setLocalTiers] = useState(() => ({ ...(playerTiersById || {}) }));
  const [localMaxSpend, setLocalMaxSpend] = useState(() => ({ ...(playerMaxSpendById || {}) }));

  // Refs per virtual scroll (solo verticale: la vista a card compatte non ha
  // più bisogno di scroll orizzontale/misurazione larghezza colonne).
  const wrapRef    = useRef(null);
  const [viewportHeight, setViewportHeight] = useState(620);
  const [scrollTop,      setScrollTop]      = useState(0);

  const isMobile = useIsMobile();
  const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;

  const roleChipOptions = (rolesMode === 'mantra' ? MANTRA_ROLES : CLASSIC_ROLES).filter(r => r !== 'all');
  const toggleRole = useCallback((value) => {
    setSelectedRoles(prev =>
      prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]
    );
  }, []);

  // Numero di gruppi di filtri (oltre alla ricerca) diversi dal default,
  // mostrato come badge sul pulsante "Filtri".
  const activeFilterCount =
    (selectedRoles.length > 0 ? 1 : 0) +
    (teamFilter !== 'all' ? 1 : 0) +
    (tierFilter !== 'all' ? 1 : 0) +
    (catFilter  !== 'all' ? 1 : 0);

  const resetFilters = useCallback(() => {
    setSearch('');
    setSelectedRoles([]);
    setTeamFilter('all');
    setCatFilter('all');
    setTierFilter('all');
    setSortMode('value_desc');
  }, []);

  // Il genitore (AuctionDetailPage) tiene sempre la mappa completa e aggiornata
  // — sia per le modifiche fatte qui (aggiornamento ottimistico), sia per
  // l'import/reset di una strategia (sostituzione totale). Va quindi
  // rispecchiata TALE E QUALE, non unita alla precedente: un merge lascerebbe
  // in vita etichette/fasce/tetti di una strategia importata prima.
  useEffect(() => { setLocalCats({ ...(playerCategoriesById || {}) }); }, [playerCategoriesById]);
  useEffect(() => { setLocalTiers({ ...(playerTiersById || {}) }); }, [playerTiersById]);
  useEffect(() => { setLocalMaxSpend({ ...(playerMaxSpendById || {}) }); }, [playerMaxSpendById]);
  useEffect(() => { setPage(1); }, [dSearch, selectedRoles, teamFilter, catFilter, tierFilter, sortMode, rolesMode]);

  const teamOptions = useMemo(() => {
    const s = new Set();
    available.forEach(p => { if (p.team) s.add(p.team); });
    return ['all', ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [available]);

  // — Filtro + sort
  const filtered = useMemo(() => {
    let list = [...available];

    if (dSearch.trim()) {
      const s = normalizeText(dSearch);
      list = list.filter(p => normalizeText(p.name || '').startsWith(s) || normalizeText(p.team || '').startsWith(s));
    }
    if (selectedRoles.length) {
      list = list.filter(p => getRoleTokens(p.role, rolesMode).some(t => selectedRoles.includes(t)));
    }
    if (teamFilter !== 'all') list = list.filter(p => String(p.team || '') === teamFilter);
    if (catFilter  !== 'all') list = list.filter(p => String(localCats?.[String(p.id)] || 'none') === catFilter);
    if (tierFilter !== 'all') {
      const wantTier = tierFilter === 'none' ? 0 : Number(tierFilter);
      list = list.filter(p => Number(localTiers?.[String(p.id)] || 0) === wantTier);
    }

    const tierRank = p => {
      const t = Number(localTiers?.[String(p.id)] || 0);
      return t >= 1 && t <= 5 ? t : 6; // senza fascia in fondo
    };

    const sorters = {
      name:               (a, b) => a.name.localeCompare(b.name),
      value_desc:         (a, b) => toNum(b.value) - toNum(a.value),
      value_asc:          (a, b) => toNum(a.value) - toNum(b.value),
      tier_asc:           (a, b) => tierRank(a) - tierRank(b),
      new_first:          (a, b) => (a.previousSeasonStats ? 1 : 0) - (b.previousSeasonStats ? 1 : 0),
      playprob_desc:      (a, b) =>
        getPlayProbabilitySortValue(playProbabilityById?.[String(b.id)]) -
        getPlayProbabilitySortValue(playProbabilityById?.[String(a.id)]),
      pma_desc:           (a, b) => toNum(marketPmaByPlayerId?.[String(b.id)]?.average) - toNum(marketPmaByPlayerId?.[String(a.id)]?.average),
      fv_desc:            (a, b) => toNum(getFv(b, 'current'))  - toNum(getFv(a, 'current')),
      mv_desc:            (a, b) => toNum(getMv(b, 'current'))  - toNum(getMv(a, 'current')),
      fv_prev_desc:       (a, b) => toNum(getFv(b, 'previous')) - toNum(getFv(a, 'previous')),
      mv_prev_desc:       (a, b) => toNum(getMv(b, 'previous')) - toNum(getMv(a, 'previous')),
      caps_desc:          (a, b) => toNum(getCaps(b, 'current')) - toNum(getCaps(a, 'current')),
      trend_desc:         (a, b) => (trendMap[String(b.id)] ?? 0) - (trendMap[String(a.id)] ?? 0),
      last5_desc:         (a, b) => getLast5Score(b) - getLast5Score(a),
      goals_for_desc:     (a, b) => toNum(getGoalsFor(b, rolesMode, 'current'))     - toNum(getGoalsFor(a, rolesMode, 'current')),
      assists_desc:       (a, b) => toNum(getAssists(b, rolesMode, 'current'))      - toNum(getAssists(a, rolesMode, 'current')),
      goals_against_desc: (a, b) => toNum(getGoalsAgainst(b, rolesMode, 'current')) - toNum(getGoalsAgainst(a, rolesMode, 'current')),
      saves_desc:         (a, b) => toNum(getSaves(b, rolesMode, 'current'))        - toNum(getSaves(a, rolesMode, 'current')),
      yc_desc:            (a, b) => toNum(getSrc(b, 'current')?.ycards ?? 0) - toNum(getSrc(a, 'current')?.ycards ?? 0),
      rc_desc:            (a, b) => toNum(getSrc(b, 'current')?.rcards ?? 0) - toNum(getSrc(a, 'current')?.rcards ?? 0),
    };

    const primarySort = sorters[sortMode] || sorters.value_desc;
    const sortWithTiebreaks = (a, b) =>
      primarySort(a, b) || sorters.value_desc(a, b);

    return [...list].sort(sortWithTiebreaks);
  }, [available, dSearch, selectedRoles, teamFilter, catFilter, tierFilter, sortMode, rolesMode, localCats, localTiers, trendMap, marketPmaByPlayerId, playProbabilityById]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage   = Math.min(page, totalPages);
  useEffect(() => { if (safePage !== page) setPage(safePage); }, [safePage, page]);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const fromItem = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const toItem   = Math.min(safePage * pageSize, totalItems);

  // — Resize / scroll sync (solo altezza viewport, per la virtualizzazione verticale)
  const syncSize = useCallback(() => {
    const w = wrapRef.current;
    if (!w) return;
    setViewportHeight(w.clientHeight || 620);
  }, []);

  useEffect(() => {
    syncSize();
    const w = wrapRef.current;
    let obs = null;
    if (typeof ResizeObserver !== 'undefined') {
      obs = new ResizeObserver(syncSize);
      if (w) obs.observe(w);
    }
    window.addEventListener('resize', syncSize);
    return () => { window.removeEventListener('resize', syncSize); if (obs) obs.disconnect(); };
  }, [syncSize, safePage, pageRows.length]);

  useEffect(() => {
    if (wrapRef.current) wrapRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [safePage]);

  const syncFromTable = useCallback(() => {
    const w = wrapRef.current;
    if (!w) return;
    setScrollTop(w.scrollTop);
  }, []);

  // — Categorie
  const openPicker = useCallback((player, el) => {
    if (readOnly) return;
    const rect = el?.getBoundingClientRect?.() || null;
    setPickerState({ playerId: String(player.id), playerName: player.name || '—', currentCategory: String(localCats?.[String(player.id)] || 'none'), anchorRect: rect });
  }, [localCats, readOnly]);

  const handleCatSelect = useCallback(async (playerId, next) => {
    const key  = String(playerId);
    const prev = String(localCats?.[key] || 'none');
    setLocalCats(c => ({ ...c, [key]: String(next || 'none') }));
    setPickerState(null);
    if (typeof onChangePlayerCategory !== 'function') return;
    try { await onChangePlayerCategory(playerId, String(next || 'none')); }
    catch { setLocalCats(c => ({ ...c, [key]: prev })); }
  }, [localCats, onChangePlayerCategory]);

  // — Fasce
  const openTierPicker = useCallback((player, el) => {
    if (readOnly) return;
    const rect = el?.getBoundingClientRect?.() || null;
    setTierPickerState({ playerId: String(player.id), playerName: player.name || '—', currentTier: Number(localTiers?.[String(player.id)] || 0), anchorRect: rect });
  }, [localTiers, readOnly]);

  const handleTierSelect = useCallback(async (playerId, next) => {
    const key  = String(playerId);
    const prev = Number(localTiers?.[key] || 0);
    const normalized = Number(next) >= 1 && Number(next) <= 5 ? Number(next) : 0;
    setLocalTiers(t => ({ ...t, [key]: normalized }));
    setTierPickerState(null);
    if (typeof onChangePlayerTier !== 'function') return;
    try { await onChangePlayerTier(playerId, normalized); }
    catch { setLocalTiers(t => ({ ...t, [key]: prev })); }
  }, [localTiers, onChangePlayerTier]);

  // — Tetto massimo di spesa
  const openMaxSpendPicker = useCallback((player, el) => {
    if (readOnly) return;
    const rect = el?.getBoundingClientRect?.() || null;
    setMaxSpendPickerState({ playerId: String(player.id), playerName: player.name || '—', currentCredits: Number(localMaxSpend?.[String(player.id)] || 0), anchorRect: rect });
  }, [localMaxSpend, readOnly]);

  const handleMaxSpendSelect = useCallback(async (playerId, next) => {
    const key  = String(playerId);
    const prev = Number(localMaxSpend?.[key] || 0);
    const normalized = Math.max(0, Math.round(Number(next) || 0));
    setLocalMaxSpend(m => ({ ...m, [key]: normalized }));
    setMaxSpendPickerState(null);
    if (typeof onChangePlayerMaxSpend !== 'function') return;
    try { await onChangePlayerMaxSpend(playerId, normalized); }
    catch { setLocalMaxSpend(m => ({ ...m, [key]: prev })); }
  }, [localMaxSpend, onChangePlayerMaxSpend]);

  // — Note
  const openNotes = useCallback(player => {
    if (readOnly) return;
    setNotesState({ playerId: String(player.id), playerName: player.name || '—', value: String(playerNotesById?.[String(player.id)] || '') });
  }, [playerNotesById, readOnly]);

  const saveNotes = useCallback(async notes => {
    if (!notesState?.playerId) return;
    await onSavePlayerNote?.(notesState.playerId, String(notes || '').trim());
    setNotesState(null);
  }, [notesState, onSavePlayerNote]);

  return (
    <section className="sv-page glass">

      {/* ── Header ── */}
      <div className="sv-header">
        <div className="sv-header__text">
          <h2>Svincolati</h2>
          <p>Giocatori ancora non acquistati.</p>
        </div>
      </div>

      {/* ── Filtri (soluzione B: ricerca+ordina sempre visibili, il resto in
          un pannello a comparsa dietro il pulsante "Filtri") ── */}
      <div className="sv-toolbar">
        <label className="sv-search-wrap">
          <Search size={15} className="sv-search-icon" />
          <input
            className="sv-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca giocatore o squadra…"
          />
          {search && (
            <button type="button" className="sv-search-clear" onClick={() => setSearch('')}>
              <X size={13} />
            </button>
          )}
        </label>

        <select className="sv-select sv-select--sort" value={sortMode} onChange={e => setSortMode(e.target.value)}>
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <button
          type="button"
          className={`sv-filters-btn ${showFilterPanel ? 'is-open' : ''}`}
          onClick={() => setShowFilterPanel(v => !v)}
          aria-expanded={showFilterPanel}
        >
          <SlidersHorizontal size={14} />
          <span>Filtri</span>
          {activeFilterCount > 0 && <span className="sv-filters-btn__count">{activeFilterCount}</span>}
        </button>
      </div>

      {showFilterPanel && (
        <div className="sv-filter-panel">
          <div className="sv-filter-panel__grid">
            <div className="sv-filter-field">
              <label>Squadra</label>
              <select className="sv-select" value={teamFilter} onChange={e => setTeamFilter(e.target.value)}>
                {teamOptions.map(t => <option key={t} value={t}>{t === 'all' ? 'Tutte le squadre' : t}</option>)}
              </select>
            </div>
            <div className="sv-filter-field">
              <label>Fascia</label>
              <select className="sv-select" value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
                {TIER_FILTERS.map(t => (
                  <option key={t} value={t}>
                    {t === 'all' ? 'Tutte le fasce' : t === 'none' ? 'Nessuna fascia' : `Fascia ${t}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="sv-filter-field sv-filter-field--full">
            <label>Ruolo <span className="sv-filter-field__hint">(selezione multipla)</span></label>
            <div className="sv-role-chips" role="group" aria-label="Filtro ruoli (selezione multipla)">
              {roleChipOptions.map(r => (
                <button
                  key={r}
                  type="button"
                  className={`sv-role-chip ${selectedRoles.includes(r) ? 'is-active' : ''}`}
                  onClick={() => toggleRole(r)}
                  aria-pressed={selectedRoles.includes(r)}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="sv-filter-field sv-filter-field--full">
            <label>Categoria</label>
            <div className="sv-cat-chip-group">
              {CATEGORY_FILTERS.map(cat => {
                const meta = getCategoryMeta(cat, customCategoryLabel);
                return (
                  <Tooltip
                    as="button"
                    key={cat}
                    type="button"
                    className={`sv-cat-chip sv-cat-chip--${cat} ${catFilter === cat ? 'is-active' : ''}`}
                    onClick={() => setCatFilter(cat)}
                    content={meta.description}
                  >
                    {meta.label}
                  </Tooltip>
                );
              })}
            </div>
          </div>

          <div className="sv-filter-panel__actions">
            <button type="button" className="sv-filter-clear-btn" onClick={resetFilters}>
              <RotateCcw size={13} />
              <span>Azzera tutti i filtri</span>
            </button>
            <button type="button" className="sv-filter-close-btn" onClick={() => setShowFilterPanel(false)}>
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* ── Lista mobile (card) ── */}
      {isMobile && (
        <div className="sv-shell">
          <div className="sv-shell__bar">
            <div className="sv-shell__info">
              <strong>{totalItems === 0 ? 'Nessun giocatore trovato' : `${fromItem}–${toItem} di ${totalItems}`}</strong>
              <span>Pag. {safePage} / {totalPages}</span>
            </div>
          </div>

          {pageRows.length > 0 ? (
            <div className="svm-list">
              {pageRows.map(player => {
                const id       = String(player.id);
                const pma      = marketPmaByPlayerId?.[id]?.average ?? null;
                const category = String(localCats?.[id] || 'none');
                const tier     = Number(localTiers?.[id] || 0);
                const maxSpend = Number(localMaxSpend?.[id] || 0);
                return (
                  <MobilePlayerCard
                    key={player.id}
                    player={player}
                    rolesMode={rolesMode}
                    category={category}
                    saving={String(savingPlayerCategoryId || '') === id}
                    tier={tier}
                    savingTier={String(savingPlayerTierId || '') === id}
                    maxSpend={maxSpend}
                    savingMaxSpend={String(savingPlayerMaxSpendId || '') === id}
                    pma={pma}
                    fvTrend={trendMap[id] ?? 0}
                    note={playerNotesById?.[id] || ''}
                    playProbability={playProbabilityById?.[id]}
                    onOpenDetail={onOpenPlayerDetail}
                    onOpenInMarket={onOpenInMarket}
                    onOpenCategoryPicker={openPicker}
                    onOpenTierPicker={openTierPicker}
                    onOpenMaxSpendPicker={openMaxSpendPicker}
                    onOpenNotesEditor={openNotes}
                    readOnly={readOnly}
                    customCategoryLabel={customCategoryLabel}
                  />
                );
              })}
            </div>
          ) : (
            <div className="sv-empty">
              Nessun giocatore con i filtri selezionati.
            </div>
          )}

          <PaginationBar page={safePage} totalPages={totalPages} totalItems={totalItems} fromItem={fromItem} toItem={toItem} onPageChange={setPage} />
        </div>
      )}

      {/* ── Tabella (desktop) ── */}
      {!isMobile && (
      <div className="sv-shell">
        <div className="sv-shell__bar">
          <div className="sv-shell__info">
            <strong>{totalItems === 0 ? 'Nessun giocatore trovato' : `${fromItem}–${toItem} di ${totalItems}`}</strong>
            <span>Pag. {safePage} / {totalPages}</span>
          </div>
          <PaginationBar page={safePage} totalPages={totalPages} totalItems={totalItems} fromItem={fromItem} toItem={toItem} onPageChange={setPage} />
        </div>

        <div ref={wrapRef} className="sv-wrap" onScroll={syncFromTable}>
          <VirtualBody
            rows={pageRows}
            scrollTop={scrollTop}
            viewportHeight={viewportHeight}
            rolesMode={rolesMode}
            savingId={savingPlayerCategoryId}
            localCategoriesById={localCats}
            savingTierId={savingPlayerTierId}
            localTiersById={localTiers}
            savingMaxSpendId={savingPlayerMaxSpendId}
            localMaxSpendById={localMaxSpend}
            marketPmaByPlayerId={marketPmaByPlayerId}
            playerNotesById={playerNotesById}
            playProbabilityById={playProbabilityById}
            readOnly={readOnly}
            highlightTiers={sortMode === 'tier_asc'}
            customCategoryLabel={customCategoryLabel}
            onOpenDetail={onOpenPlayerDetail}
            onOpenInMarket={onOpenInMarket}
            onOpenCategoryPicker={openPicker}
            onOpenTierPicker={openTierPicker}
            onOpenMaxSpendPicker={openMaxSpendPicker}
            onOpenNotesEditor={openNotes}
          />

          {pageRows.length === 0 && (
            <div className="sv-empty">
              Nessun giocatore con i filtri selezionati.
            </div>
          )}
        </div>

        <PaginationBar page={safePage} totalPages={totalPages} totalItems={totalItems} fromItem={fromItem} toItem={toItem} onPageChange={setPage} />
      </div>
      )}

      {/* ── Popovers ── */}
      <CategoryPopover
        open={Boolean(pickerState)}
        anchorRect={pickerState?.anchorRect || null}
        playerName={pickerState?.playerName || '—'}
        currentCategory={pickerState?.currentCategory || 'none'}
        saving={String(savingPlayerCategoryId || '') === String(pickerState?.playerId || '')}
        onSelect={cat => handleCatSelect(pickerState.playerId, cat)}
        onClose={() => setPickerState(null)}
        customLabel={customCategoryLabel}
        canRenameCustom={!readOnly}
        onRequestRenameCustom={openRenameCustomCategory}
      />

      {renamingCustomCategory && (
        <RenameCustomCategoryModal
          initialLabel={customCategoryLabel}
          onClose={() => setRenamingCustomCategory(false)}
          onSubmit={submitRenameCustomCategory}
          saving={savingCustomCategoryLabel}
        />
      )}

      <TierPopover
        open={Boolean(tierPickerState)}
        anchorRect={tierPickerState?.anchorRect || null}
        playerName={tierPickerState?.playerName || '—'}
        currentTier={tierPickerState?.currentTier || 0}
        saving={String(savingPlayerTierId || '') === String(tierPickerState?.playerId || '')}
        onSelect={tier => handleTierSelect(tierPickerState.playerId, tier)}
        onClose={() => setTierPickerState(null)}
      />

      <MaxSpendPopover
        open={Boolean(maxSpendPickerState)}
        anchorRect={maxSpendPickerState?.anchorRect || null}
        playerName={maxSpendPickerState?.playerName || '—'}
        currentCredits={maxSpendPickerState?.currentCredits || 0}
        saving={String(savingPlayerMaxSpendId || '') === String(maxSpendPickerState?.playerId || '')}
        onSelect={credits => handleMaxSpendSelect(maxSpendPickerState.playerId, credits)}
        onClose={() => setMaxSpendPickerState(null)}
      />

      {notesState && typeof document !== 'undefined' && createPortal(
        <div className="sv-notes-layer" onClick={() => setNotesState(null)}>
          <div
            className="sv-notes-modal glass"
            role="dialog"
            aria-label={`Note per ${notesState.playerName}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="sv-notes-head">
              <div>
                <strong>{notesState.playerName}</strong>
                <span>Note e commenti personali</span>
              </div>
              <button type="button" className="sv-notes-close" onClick={() => setNotesState(null)}><X size={16} /></button>
            </div>

            <textarea
              className="sv-notes-textarea"
              defaultValue={notesState.value}
              placeholder="Scrivi qui le tue note su questo giocatore…"
              rows={7}
            />

            <div className="sv-notes-actions">
              <button type="button" className="sv-btn sv-btn--ghost" onClick={() => setNotesState(null)}>Annulla</button>
              <button
                type="button"
                className="sv-btn sv-btn--primary"
                onClick={() => {
                  const ta = document.querySelector('.sv-notes-textarea');
                  saveNotes(ta?.value || '');
                }}
              >
                Salva nota
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
